
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Crown, Zap, Sparkles } from 'lucide-react';
import { useSubscription } from '@/hooks/useSubscription';
import { toast } from 'sonner';

interface UpgradePromptProps {
  feature: string;
  description?: string;
}

const UpgradePrompt: React.FC<UpgradePromptProps> = ({ 
  feature, 
  description = "This feature is available in the Pro plan" 
}) => {
  const { upgradeToPro, loading } = useSubscription();

  const handleUpgrade = async () => {
    const result = await upgradeToPro();
    if (result.success) {
      toast.success('🎉 Welcome to YakStak Pro!', {
        description: 'You now have access to all AI-powered features.'
      });
    } else {
      toast.error('Upgrade failed', {
        description: 'Please try again or contact support.'
      });
    }
  };

  return (
    <Card className="max-w-md mx-auto border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
      <CardHeader className="text-center">
        <div className="mx-auto w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mb-4">
          <Crown className="w-8 h-8 text-white" />
        </div>
        <CardTitle className="text-xl">
          <Badge className="mb-2 bg-gradient-to-r from-purple-500 to-pink-500">
            <Sparkles className="w-3 h-3 mr-1" />
            Pro Feature
          </Badge>
          <div>Unlock {feature}</div>
        </CardTitle>
      </CardHeader>
      <CardContent className="text-center space-y-4">
        <p className="text-gray-600">{description}</p>
        <div className="bg-white/50 rounded-lg p-4">
          <div className="text-2xl font-bold text-gray-900">£12/month</div>
          <div className="text-sm text-gray-600">Full access to AI features</div>
        </div>
        <Button 
          onClick={handleUpgrade}
          disabled={loading}
          className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
          size="lg"
        >
          {loading ? (
            'Processing...'
          ) : (
            <>
              Upgrade to Pro
              <Zap className="ml-2 w-4 h-4" />
            </>
          )}
        </Button>
        <p className="text-xs text-gray-500">
          Upgrade instantly • Cancel anytime
        </p>
      </CardContent>
    </Card>
  );
};

export default UpgradePrompt;
